// React imports first
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Third-party library imports next
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import {
  SettingsRounded,
  DashboardRounded,
  TranslateRounded,
  InterpreterModeRounded,
  RecordVoiceOverRounded,
  PeopleRounded,
  GroupsRounded,
  RequestPageRounded,
  PriceCheckRounded,
  PointOfSaleRounded,
  AssessmentRounded,
  VideoLibraryRounded,
} from "@mui/icons-material";
import MenuOpenRoundedIcon from "@mui/icons-material/MenuOpenRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import HelpCenterRoundedIcon from "@mui/icons-material/HelpCenterRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";

// Local imports last
import FlexBetween from "./FlexBetween";
import { useStateContext } from "../contexts/ContextProvider";
import Chip from "@mui/material/Chip";
import api from "../services/api";
import { getOpenVideoCommentCount } from "../services/videoLibraryApi";
import { useWebSocket } from "../contexts/WebSocketContext";

// Constants
const ANIMATION_SPEED = 50;

// Styled HtmlTooltip component
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.background.alt
        : theme.palette.background.paper,
    color: theme.palette.primary.main,
    maxWidth: 380,
    boxShadow: theme.shadows[1],
    fontSize: theme.typography.pxToRem(14),
    padding: "10px 12px",
  },
}));

// 2026-10-30 (Amine): one assignment inside a summary tooltip. Rendered as a
// card so consecutive rows read as separate items instead of one dense block of
// text, and so the project number has room to be a real link to the profile.
const SummaryItemCard = ({
  projectLabel,
  title,
  subtitle,
  meta,
  onActivate,
}) => {
  const theme = useTheme();

  const activate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onActivate?.();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        px: 1.25,
        py: 0.875,
        mb: 0.75,
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        transition: "border-color 0.15s ease",
        "&:last-of-type": { mb: 0 },
        "&:hover": { borderColor: theme.palette.primary.main },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box
          component="span"
          role="link"
          tabIndex={0}
          onClick={activate}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") activate(event);
          }}
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 700,
            cursor: "pointer",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
              borderRadius: "4px",
            },
          }}
        >
          {projectLabel}
        </Box>

        {meta ? (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {meta}
          </Typography>
        ) : null}
      </Box>

      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.primary,
          fontWeight: 600,
          lineHeight: 1.35,
        }}
      >
        {title || "Untitled"}
      </Typography>

      {subtitle ? (
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary, lineHeight: 1.3 }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
};

// 2026-10-30 (Amine): the summary badges open their list pre-filtered to
// exactly the assignments they counted. `ids` is dropped above the cap so a
// large month cannot overflow the URL, and a payload with no id list (an older
// cached response) keeps the plain list behaviour — `?ids=` is never emitted
// empty.
const SUMMARY_FILTER_MAX_IDS = 300;

const buildSummaryFilterUrl = (path, itemIds, itemMonths) => {
  const ids = Array.isArray(itemIds)
    ? itemIds.filter((id) => id !== null && id !== undefined)
    : [];
  if (ids.length === 0) return path;

  const months = Array.isArray(itemMonths)
    ? itemMonths.filter((month) => typeof month === "string" && month)
    : [];

  const params = new URLSearchParams();
  if (ids.length <= SUMMARY_FILTER_MAX_IDS) {
    params.set("ids", ids.join(","));
  }
  if (months.length > 0) {
    params.set("months", months.join(","));
  }

  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

// Custom menu item component
const MenuItem = ({
  icon,
  text,
  active,
  link,
  onClick,
  isCollapsed,
  textVisible,
  badgeCount,
  badgeTooltip,
  badgeSeverity,
  badgeInteractive,
  onBadgeClick,
}) => {
  const theme = useTheme();
  const isActive = active === link;

  // 2026-10-30 (Amine): the deadline / interpretation tooltips contain links
  // and a filter action, so their open state is controlled — the sidebar
  // survives the route change and an uncontrolled popper would stay on screen
  // after navigating.
  const [badgeTooltipOpen, setBadgeTooltipOpen] = useState(false);

  return (
    <Box
      role={link ? "link" : undefined}
      tabIndex={link ? 0 : undefined}
      aria-label={link ? text : undefined}
      aria-current={isActive && link ? "page" : undefined}
      onClick={onClick}
      onKeyDown={
        link
          ? (event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        borderRadius: "12px",
        mx: isCollapsed ? 1.25 : 2,
        my: 0.25,
        py: 1,
        px: 1.25,
        backgroundColor: isActive ? theme.palette.primary.main : "transparent",
        color: isActive
          ? theme.palette.primary.main
          : theme.palette.primary.text2,
        transition: "background-color 0.3s ease",
        "&:hover": {
          backgroundColor: isActive
            ? theme.palette.primary.buttonHoverSelected
            : theme.palette.primary.buttonHover,
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      {/* Icon container - fixed width to maintain position */}
      <Box
        sx={{
          width: "40px",
          height: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "flex-start",
          color: isActive
            ? theme.palette.background.default
            : theme.palette.primary.text2,
        }}
      >
        {icon}
      </Box>

      {/* Text container with fade effect */}
      {!isCollapsed && (
        <Box
          sx={{
            ml: 1,
            flexGrow: 1,
            opacity: textVisible ? 1 : 0,
            transition: `opacity ${ANIMATION_SPEED}ms ease-in-out`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: isActive
                ? theme.palette.background.default
                : theme.palette.primary.text2,
            }}
          >
            {text}
          </Typography>
          {badgeCount > 0 &&
            (() => {
              const badgeChip = (
                <Chip
                  label={badgeCount}
                  size="small"
                  sx={{
                    height: "20px",
                    borderRadius: "8px",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    bgcolor: isActive
                      ? theme.palette.background.default
                      : badgeSeverity === "critical"
                        ? theme.palette.error.main
                        : badgeSeverity === "warning"
                          ? theme.palette.warning.main
                          : theme.palette.primary.main,
                    color: isActive
                      ? badgeSeverity === "critical"
                        ? theme.palette.error.main
                        : badgeSeverity === "warning"
                          ? theme.palette.warning.main
                          : theme.palette.primary.main
                      : theme.palette.background.default,
                    minWidth: "20px",
                    "& .MuiChip-label": {
                      px: 0.75,
                    },
                  }}
                />
              );

              // 2026-10-20 (Amine): the Expenses badge explains itself on
              // hover ("We have X expenses still not finalized in ...").
              // 2026-10-30 (Amine): the deadline / interpretation badges carry
              // clickable project numbers, which requires `interactive` — MUI
              // defaults the popper to pointer-events: none. Clicking inside
              // the popper closes it so it never survives the navigation.
              return badgeTooltip ? (
                <HtmlTooltip
                  title={
                    badgeInteractive ? (
                      <Box
                        onClick={() => setBadgeTooltipOpen(false)}
                        sx={{ cursor: "default" }}
                      >
                        {badgeTooltip}
                      </Box>
                    ) : (
                      badgeTooltip
                    )
                  }
                  placement="bottom"
                  // MUI v6 removed `interactive`: tooltips are interactive by
                  // default and you opt OUT. Passing `interactive` leaked a
                  // non-boolean attribute onto the DOM.
                  disableInteractive={!badgeInteractive}
                  open={badgeInteractive ? badgeTooltipOpen : undefined}
                  onOpen={
                    badgeInteractive
                      ? () => setBadgeTooltipOpen(true)
                      : undefined
                  }
                  onClose={
                    badgeInteractive
                      ? () => setBadgeTooltipOpen(false)
                      : undefined
                  }
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      cursor: onBadgeClick ? "pointer" : "default",
                    }}
                    onClick={
                      onBadgeClick
                        ? (event) => {
                            setBadgeTooltipOpen(false);
                            onBadgeClick(event);
                          }
                        : undefined
                    }
                  >
                    {badgeChip}
                  </Box>
                </HtmlTooltip>
              ) : (
                badgeChip
              );
            })()}
          {/* Removed arrow from selected items */}
        </Box>
      )}
    </Box>
  );
};

// Custom section header component
const SectionHeader = ({ text, isCollapsed, textVisible }) => {
  const theme = useTheme();

  if (isCollapsed) return null;

  return (
    <Box
      sx={{
        mx: 2,
        mt: 0.5, // Reduced from 2 to 1 (half spacing)
        mb: 0.125, // Reduced from 0.5 to 0.25 (half spacing)
        opacity: textVisible ? 1 : 0,
        transition: `opacity ${ANIMATION_SPEED}ms ease-in-out`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.primary.main,
          fontWeight: "bold",
          fontSize: 13,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

const Sidebar = ({ user, drawerWidth, isNonMobile }) => {
  // Define the collapsed drawer width (exactly 25% of full width)
  const collapsedDrawerWidth = Math.floor(drawerWidth * 0.25);

  // Use the context for sidebar state management
  const {
    activeMenu,
    setActiveMenu,
    isCollapsed: contextIsCollapsed,
    setIsCollapsed: setContextIsCollapsed,
  } = useStateContext();

  // Get theme
  const theme = useTheme();

  // 2026-10-30 (Amine): hoisted so the summary tooltip builders (which render
  // project links) can navigate without depending on declaration order.
  const navigate = useNavigate();

  // WebSocket instance (provided by WebSocketProvider)
  const { socket } = useWebSocket();

  // Local state for task count
  const [taskCount, setTaskCount] = useState(0);
  // Local state for the unbilled-assignments badge (Invoices item)
  const [unbilledCount, setUnbilledCount] = useState(0);
  // Local state for the draft-expenses badge (Expenses item)
  const [draftExpensesCount, setDraftExpensesCount] = useState(0);
  // Local state for the open-comments badge (Video Library item)
  const [videoCommentCount, setVideoCommentCount] = useState(0);
  const [draftExpensesMonths, setDraftExpensesMonths] = useState([]);
  // 2026-10-28 (Amine): outstanding-deadline counts for the Translations and
  // Other Services items — work that is not Delivered/Completed and whose
  // deadline is today or already past.
  const [deadlineSummary, setDeadlineSummary] = useState({
    translations: {
      dueToday: 0,
      pastDue: 0,
      outstanding: 0,
      items: [],
      itemsTruncated: false,
      itemIds: [],
      itemMonths: [],
    },
    other: {
      dueToday: 0,
      pastDue: 0,
      outstanding: 0,
      items: [],
      itemsTruncated: false,
      itemIds: [],
      itemMonths: [],
    },
    dueTodayTotal: 0,
    pastDueTotal: 0,
  });

  // 2026-10-30 (Amine): month-end interpretation completion. Accounting can
  // only invoice an interpretation once it is marked Completed, so the
  // Interpretations item carries the count of work still open for the month
  // being closed out. The server owns the window logic — `active: false`
  // means we are not inside a month-end/month-start window and nothing
  // should render at all.
  const [interpretationCompletion, setInterpretationCompletion] = useState({
    active: false,
    count: 0,
    items: [],
    itemsTruncated: false,
    itemIds: [],
    itemMonths: [],
    targetMonthLabel: "",
  });

  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Set mounted ref on mount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch task count from API
  const fetchTaskCount = useCallback(async () => {
    try {
      const userId = user?.id;
      if (!userId) {
        return;
      }

      const params = new URLSearchParams();
      params.append("memberId", userId);
      params.append("hideAutomatedTasks", "false");

      const response = await api.get(`/tasks/count?${params.toString()}`);

      if (isMountedRef.current && response.data?.count !== undefined) {
        setTaskCount(response.data.count);
      }
    } catch (error) {}
  }, [user]);

  // WebSocket listener: update task count in real-time
  useEffect(() => {
    if (!socket || !user?.id) return;

    const handleTaskCount = (data) => {
      if (data.userId === user.id && isMountedRef.current) {
        setTaskCount(data.count);
      }
    };

    // Ask server to make sure we're in the right room (extra safety)
    socket.emit("join", { userId: user.id });
    socket.on("taskCountUpdated", handleTaskCount);

    // Also fetch once on connect as a fallback
    fetchTaskCount();

    return () => {
      socket.off("taskCountUpdated", handleTaskCount);
    };
  }, [socket, user?.id, fetchTaskCount]);

  // Temporary: Fetch initial count on mount
  useEffect(() => {
    if (user?.id) {
      fetchTaskCount();
    }
  }, [user?.id, fetchTaskCount]);

  // Fetch the unbilled-assignments count (previous months, not invoiced)
  const fetchUnbilledCount = useCallback(async () => {
    try {
      const response = await api.get("/invoices/unbilled-count");
      if (isMountedRef.current && response.data?.count !== undefined) {
        setUnbilledCount(response.data.count);
      }
    } catch (error) {}
  }, []);

  // WebSocket listener: refresh the unbilled badge when invoices/services change
  useEffect(() => {
    if (!socket) return;

    const handleUnbilledCount = () => {
      fetchUnbilledCount();
    };

    socket.on("unbilledCountUpdated", handleUnbilledCount);
    fetchUnbilledCount();

    return () => {
      socket.off("unbilledCountUpdated", handleUnbilledCount);
    };
  }, [socket, fetchUnbilledCount]);

  // 2026-10-20 (Amine): Expenses badge — drafts left unfinalized in PREVIOUS
  // months. Current-month drafts are normal, so the server excludes them.
  const fetchDraftExpensesCount = useCallback(async () => {
    // Expenses is an Admin-only screen — skip the call entirely for members
    // instead of triggering a guaranteed 403 on every navigation.
    if ((user?.Role || "Member") !== "Admin") return;

    try {
      const response = await api.get("/expenses/draft-count");
      if (isMountedRef.current && response.data) {
        setDraftExpensesCount(Number(response.data.count) || 0);
        setDraftExpensesMonths(
          Array.isArray(response.data.months) ? response.data.months : [],
        );
      }
    } catch (error) {
      // Members receive 403 on expense routes — the badge simply stays empty.
    }
  }, [user?.Role]);

  // Load immediately on mount. The badge must never depend on the socket
  // being connected — previously the first fetch lived inside the socket
  // effect, so a slow/reconnecting socket left it empty until a full page
  // reload (2026-10-20).
  useEffect(() => {
    fetchDraftExpensesCount();
  }, [fetchDraftExpensesCount]);

  // Returning to the tab should also refresh it (e.g. expense added in
  // another window or the socket event was missed).
  useEffect(() => {
    const handleWindowFocus = () => {
      fetchDraftExpensesCount();
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => window.removeEventListener("focus", handleWindowFocus);
  }, [fetchDraftExpensesCount]);

  // Live refresh from the server after any expense change.
  useEffect(() => {
    if (!socket) return undefined;

    const handleDraftExpensesCount = () => {
      fetchDraftExpensesCount();
    };

    socket.on("expenseDraftCountUpdated", handleDraftExpensesCount);

    return () => {
      socket.off("expenseDraftCountUpdated", handleDraftExpensesCount);
    };
  }, [socket, fetchDraftExpensesCount]);

  // 2026-10-28 (Amine): outstanding Deadline counts for Translations and Other
  // Services. Server-side so "today" is resolved in the business timezone
  // rather than the browser's. Admins and Members both see this.
  const { pathname: deadlinePathname } = useLocation();

  const fetchDeadlineSummary = useCallback(async () => {
    try {
      const response = await api.get("/services/deadline-summary");
      if (isMountedRef.current && response.data) {
        setDeadlineSummary({
          translations: response.data.translations || {
            dueToday: 0,
            pastDue: 0,
            outstanding: 0,
          },
          other: response.data.other || { dueToday: 0, pastDue: 0, outstanding: 0 },
          dueTodayTotal: Number(response.data.dueTodayTotal) || 0,
          pastDueTotal: Number(response.data.pastDueTotal) || 0,
        });
      }
    } catch (error) {
      // A failed count leaves the badges empty rather than blocking the sidebar.
    }
  }, []);

  // Returning to the tab refreshes it, in case the socket event was missed.
  useEffect(() => {
    const handleWindowFocus = () => {
      fetchDeadlineSummary();
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => window.removeEventListener("focus", handleWindowFocus);
  }, [fetchDeadlineSummary]);

  // Live refresh whenever a service is saved, completed or deleted.
  // `deadlineSummaryUpdated` (2026-10-29) is emitted by the server whenever a
  // field feeding the summary changes — marking work Delivered included — so
  // the badge drops immediately instead of waiting for the poll below.
  useEffect(() => {
    if (!socket) return undefined;

    const handleServiceChange = () => {
      fetchDeadlineSummary();
    };

    socket.on("assignment:updated", handleServiceChange);
    socket.on("notification:created", handleServiceChange);
    socket.on("deadlineSummaryUpdated", handleServiceChange);

    return () => {
      socket.off("assignment:updated", handleServiceChange);
      socket.off("notification:created", handleServiceChange);
      socket.off("deadlineSummaryUpdated", handleServiceChange);
    };
  }, [socket, fetchDeadlineSummary]);

  // Safety net: deadlines roll from "due today" to "past due" purely with the
  // clock, with no write to trigger an event. Polling keeps that honest
  // without hammering the API — skipped entirely while the tab is hidden.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchDeadlineSummary();
      }
    }, 5 * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [fetchDeadlineSummary]);

  // Refresh on navigation so the badge tracks the list the user just edited.
  useEffect(() => {
    fetchDeadlineSummary();
  }, [deadlinePathname, fetchDeadlineSummary]);

  // 2026-10-30 (Amine): interpretations not yet marked Completed for the month
  // being closed out. Same shape and refresh strategy as the deadline summary
  // above; a failure only ever leaves the badge empty.
  const fetchInterpretationCompletion = useCallback(async () => {
    try {
      const response = await api.get(
        "/services/interpretation-completion-summary",
      );
      if (isMountedRef.current && response.data) {
        setInterpretationCompletion({
          active: Boolean(response.data.active),
          count: Number(response.data.count) || 0,
          items: Array.isArray(response.data.items) ? response.data.items : [],
          itemsTruncated: Boolean(response.data.itemsTruncated),
          // Uncapped ids + months drive the badge's "filter the grid" action.
          itemIds: Array.isArray(response.data.itemIds)
            ? response.data.itemIds
            : [],
          itemMonths: Array.isArray(response.data.itemMonths)
            ? response.data.itemMonths
            : [],
          targetMonthLabel: String(response.data.targetMonthLabel || ""),
        });
      }
    } catch (error) {
      // A failed count leaves the badge empty rather than blocking the sidebar.
    }
  }, []);

  // Returning to the tab refreshes it, in case the socket event was missed.
  useEffect(() => {
    const handleWindowFocus = () => {
      fetchInterpretationCompletion();
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => window.removeEventListener("focus", handleWindowFocus);
  }, [fetchInterpretationCompletion]);

  // Live refresh whenever a service is saved, completed or deleted.
  // `interpretationCompletionUpdated` (2026-10-30) is emitted by the server
  // whenever a field feeding the summary changes — marking an interpretation
  // Completed included — so the badge drops immediately.
  useEffect(() => {
    if (!socket) return undefined;

    const handleInterpretationCompletionChange = () => {
      fetchInterpretationCompletion();
    };

    socket.on("assignment:updated", handleInterpretationCompletionChange);
    socket.on("notification:created", handleInterpretationCompletionChange);
    socket.on(
      "interpretationCompletionUpdated",
      handleInterpretationCompletionChange,
    );

    return () => {
      socket.off("assignment:updated", handleInterpretationCompletionChange);
      socket.off("notification:created", handleInterpretationCompletionChange);
      socket.off(
        "interpretationCompletionUpdated",
        handleInterpretationCompletionChange,
      );
    };
  }, [socket, fetchInterpretationCompletion]);

  // Safety net: the target month rolls over purely with the clock, with no
  // write to trigger an event. Skipped entirely while the tab is hidden.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchInterpretationCompletion();
      }
    }, 5 * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [fetchInterpretationCompletion]);

  // Refresh on navigation so the badge tracks the list the user just edited.
  useEffect(() => {
    fetchInterpretationCompletion();
  }, [deadlinePathname, fetchInterpretationCompletion]);

  // Video Library badge: comments still open (neither deleted nor marked done).
  const fetchVideoCommentCount = useCallback(async () => {
    // Video Library is Admin-only; members would just get a 403.
    if ((user?.Role || "Member") !== "Admin") return;

    try {
      const response = await getOpenVideoCommentCount();
      if (isMountedRef.current && response.data?.count !== undefined) {
        setVideoCommentCount(Number(response.data.count) || 0);
      }
    } catch {
      // A failed count leaves the badge empty rather than blocking the sidebar.
    }
  }, [user?.Role]);

  useEffect(() => {
    fetchVideoCommentCount();
  }, [fetchVideoCommentCount]);

  // The profile and the comments dialog dispatch this after resolving, deleting
  // or replying, so the badge tracks the screen the admin is currently on.
  useEffect(() => {
    const handleVideoCommentsChanged = () => {
      fetchVideoCommentCount();
    };

    window.addEventListener("videoCommentsChanged", handleVideoCommentsChanged);
    return () =>
      window.removeEventListener("videoCommentsChanged", handleVideoCommentsChanged);
  }, [fetchVideoCommentCount]);

  // Any other tab (or a reviewer on the public page) adding, deleting or resolving
  // a comment pushes this so the badge moves without a navigation.
  useEffect(() => {
    if (!socket) return undefined;

    const handleVideoCommentCount = () => {
      fetchVideoCommentCount();
    };

    socket.on("videoCommentCountUpdated", handleVideoCommentCount);

    return () => {
      socket.off("videoCommentCountUpdated", handleVideoCommentCount);
    };
  }, [socket, fetchVideoCommentCount]);

  const videoCommentTooltip = videoCommentCount
    ? `${videoCommentCount} comment${videoCommentCount === 1 ? "" : "s"} still open`
    : "";

  const draftExpensesTooltip = (() => {
    if (!draftExpensesCount) return "";

    const monthLabel = (entry) => {
      if (!entry?.year || !entry?.month) return "";
      return new Date(entry.year, entry.month - 1, 1).toLocaleString("en-US", {
        month: "long",
      });
    };

    const entries = (draftExpensesMonths || []).filter(
      (entry) => Number(entry?.count) > 0,
    );
    const noun = draftExpensesCount === 1 ? "expense" : "expenses";

    if (entries.length === 1) {
      const label = monthLabel(entries[0]);
      return label
        ? `We have ${draftExpensesCount} ${noun} still not finalized in ${label}`
        : `We have ${draftExpensesCount} ${noun} still not finalized`;
    }

    if (entries.length > 1) {
      const detail = entries
        .map((entry) => {
          const label = monthLabel(entry);
          return label ? `${label} (${entry.count})` : null;
        })
        .filter(Boolean)
        .join(", ");
      return detail
        ? `We have ${draftExpensesCount} ${noun} still not finalized: ${detail}`
        : `We have ${draftExpensesCount} ${noun} still not finalized`;
    }

    return `We have ${draftExpensesCount} ${noun} still not finalized`;
  })();

  // 2026-10-21: Server health badge. The collector writes a snapshot every
  // minute, so a 60s read keeps this current without a socket round-trip.
  const [serverIssues, setServerIssues] = useState([]);
  const [serverHealth, setServerHealth] = useState("unknown");

  const fetchServerHealth = useCallback(async () => {
    if ((user?.Role || "Member") !== "Admin") return;

    try {
      const response = await api.get("/server/status");
      if (!isMountedRef.current) return;
      setServerIssues(Array.isArray(response.data?.issues) ? response.data.issues : []);
      setServerHealth(response.data?.healthStatus || "unknown");
    } catch (error) {
      // 403 for members or the API being down — leave the badge empty.
    }
  }, [user?.Role]);

  useEffect(() => {
    fetchServerHealth();

    const intervalId = setInterval(fetchServerHealth, 60000);
    return () => clearInterval(intervalId);
  }, [fetchServerHealth]);

  const serverBadgeSeverity =
    serverHealth === "critical"
      ? "critical"
      : serverHealth === "warning"
        ? "warning"
        : null;

  const serverTooltip = (() => {
    if (serverIssues.length === 0) return "";

    const shown = serverIssues.slice(0, 3).map((issue) => issue.message);
    const extra = serverIssues.length - shown.length;

    return `${shown.join(" • ")}${extra > 0 ? ` • +${extra} more` : ""}`;
  })();

  // 2026-10-28 (Amine): Translations / Other Services badges count work that is
  // still open and whose Deadline has arrived. Red once anything is overdue,
  // amber while it is only due today.
  const deadlineSeverity = (counts) => {
    if (!counts) return null;
    if (Number(counts.pastDue) > 0) return "critical";
    if (Number(counts.dueToday) > 0) return "warning";
    return null;
  };

  // 2026-10-29 (Amine): the counts alone did not say WHICH projects were late,
  // so the tooltip now names them. The server sorts past-due first and caps the
  // preview; `itemsTruncated` says more exist beyond it.
  const formatDeadlineDate = (value) => {
    const raw = String(value || "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

    const [year, month, day] = raw.split("-").map(Number);
    const parsed = new Date(year, month - 1, day);
    if (Number.isNaN(parsed.getTime())) return raw;

    return parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // 2026-10-30 (Amine): a project number inside a summary tooltip is a real
  // link to that assignment's profile — primary colour, underlined on hover,
  // keyboard reachable, and it always stops the click so the surrounding
  // badge / menu-item action does not fire too.
  const deadlineTooltip = (counts, bucket) => {
    if (!counts) return "";

    const pastDue = Number(counts.pastDue) || 0;
    const dueToday = Number(counts.dueToday) || 0;
    if (!pastDue && !dueToday) return "";

    const parts = [];
    if (pastDue) {
      parts.push(`${pastDue} past due`);
    }
    if (dueToday) {
      parts.push(`${dueToday} due today`);
    }

    const items = Array.isArray(counts.items) ? counts.items : [];
    if (items.length === 0) return parts.join(" • ");

    // 2026-10-30 (Amine): each project number opens that assignment's profile,
    // so the tooltip is `interactive` (see MenuItem) and the bucket decides
    // which list route a row belongs to. Rows are rendered as cards so they
    // stay visually separated.
    const profileBase =
      bucket === "other" ? "/otherServices" : "/translations";

    return (
      <Box sx={{ py: 0.25 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, display: "block", mb: 0.75 }}
        >
          {parts.join(" • ")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: 340,
            overflowY: "auto",
            pr: 0.25,
          }}
        >
          {items.map((item) => (
            <SummaryItemCard
              key={`${item.id}-${item.bucket}`}
              projectLabel={
                item.ProjectNumber
                  ? `#${item.ProjectNumber}`
                  : `Service ${item.id}`
              }
              title={String(item.Name || "").trim()}
              subtitle={String(item.ClientName || "").trim()}
              meta={formatDeadlineDate(item.Deadline || item.Date)}
              onActivate={() => navigate(`${profileBase}/${item.id}`)}
            />
          ))}
        </Box>

        {counts.itemsTruncated && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.75,
              fontStyle: "italic",
              color: theme.palette.text.secondary,
            }}
          >
            + more not shown
          </Typography>
        )}
      </Box>
    );
  };

  // 2026-10-30 (Amine): the count alone does not say WHICH interpretations are
  // still open, so the tooltip names them — project number, details, client
  // and date. Sample shape: { id, ProjectNumber, Details, Date, Status,
  // ClientName }. The server caps the preview and sets `itemsTruncated`.
  const interpretationTooltip = (summary) => {
    if (!summary || !summary.active) return "";

    const count = Number(summary.count) || 0;
    if (!count) return "";

    const header = summary.targetMonthLabel
      ? `${count} not completed · ${summary.targetMonthLabel}`
      : `${count} not completed`;

    const items = Array.isArray(summary.items) ? summary.items : [];
    if (items.length === 0) return header;

    return (
      <Box sx={{ py: 0.25 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, display: "block", mb: 0.75 }}
        >
          {header}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: 340,
            overflowY: "auto",
            pr: 0.25,
          }}
        >
          {items.map((item) => (
            <SummaryItemCard
              key={item.id}
              projectLabel={
                item.ProjectNumber
                  ? `#${item.ProjectNumber}`
                  : `Interpretation ${item.id}`
              }
              // `Details` is the human-readable service description; `Name` is
              // the legacy field on some older rows.
              title={String(item.Details || item.Name || "").trim()}
              subtitle={String(item.ClientName || "").trim()}
              meta={item.Date ? formatDeadlineDate(item.Date) : ""}
              onActivate={() => navigate(`/interpretations/${item.id}`)}
            />
          ))}
        </Box>

        {summary.itemsTruncated && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.75,
              fontStyle: "italic",
              color: theme.palette.text.secondary,
            }}
          >
            + more not shown
          </Typography>
        )}
      </Box>
    );
  };

  // Local state to control the actual collapse state with delay
  const [isCollapsed, setIsCollapsed] = useState(contextIsCollapsed);

  // State to control text fade animations
  const [textVisible, setTextVisible] = useState(!contextIsCollapsed);

  // State to prevent multiple clicks during animation
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle context collapse state changes with sequential animation
  useEffect(() => {
    // If we're already in the desired state, do nothing
    if (
      contextIsCollapsed === isCollapsed &&
      textVisible === !contextIsCollapsed
    ) {
      return;
    }

    setIsAnimating(true);

    if (contextIsCollapsed) {
      // Step 1: Fade out text
      setTextVisible(false);

      // Step 2: After text fade out completes, collapse drawer
      const timer = setTimeout(() => {
        setIsCollapsed(true);
        setIsAnimating(false);
      }, ANIMATION_SPEED); // Wait for text fade out

      return () => clearTimeout(timer);
    } else {
      // When expanding, first expand drawer
      setIsCollapsed(false);

      // Then fade in text after drawer expands
      const timer = setTimeout(() => {
        setTextVisible(true);
        setIsAnimating(false);
      }, ANIMATION_SPEED); // Wait for drawer expansion

      return () => clearTimeout(timer);
    }
  }, [contextIsCollapsed, isCollapsed, textVisible]);

  // Calculate the current drawer width based on collapsed state
  const currentDrawerWidth = isCollapsed ? collapsedDrawerWidth : drawerWidth;

  const navItems = [
    {
      text: "WORKSPACE",
      link: "",
      icon: null,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      // 2026-10-30 (Amine): /dashboard is now the operational "today" view
      // (Dashboard_NEW). The old period dashboard retired to /dashboard-legacy
      // — reachable, but deliberately NOT in the sidebar, so nobody falls back
      // into it out of habit.
      text: "Dashboard",
      link: "dashboard",
      params: "",
      icon: <DashboardRounded />,
      role: ["Admin", "Member"],
    },
    {
      text: "Calendar",
      link: "calendar",
      icon: <CalendarMonthRoundedIcon />, //CalendarMonthRoundedIcon
      role: ["Admin", "Member"],
    },
    {
      text: "Tasks",
      link: "tasks",
      icon: <FormatListBulletedRoundedIcon />,
      role: ["Admin", "Member"],
    },
    {
      text: "SERVICES",
      link: "services",
      icon: null,
      role: ["Admin"], // Clickable for Admin - navigates to All Services
    },
    {
      text: "SERVICES",
      link: "",
      icon: null,
      role: ["Member"], // Non-clickable header for Members
    },
    {
      text: "Interpretations",
      link: "interpretations",
      icon: <RecordVoiceOverRounded />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "Translations",
      link: "translations",
      icon: <TranslateRounded />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "Other Services",
      link: "otherServices",
      icon: <MoreHorizRoundedIcon />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "CONNECTIONS",
      link: "",
      icon: null,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "Clients",
      link: "clients",
      icon: <PeopleRounded />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "Linguists",
      link: "linguists",
      icon: <InterpreterModeRounded />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "MANAGEMENT",
      link: "",
      icon: null,
      role: ["Admin"], // Admin only section
    },
    {
      text: "Video Library",
      link: "video-library",
      icon: <VideoLibraryRounded />,
      role: ["Admin"],
    },
    {
      text: "Linguists' Payments",
      link: "linguistsPayments",
      icon: <PriceCheckRounded />,
      role: ["Admin"], // Admin only
    },
    {
      text: "Expenses",
      link: "expenses",
      icon: <PointOfSaleRounded />,
      role: ["Admin"], // Admin only
    },
    {
      text: "Invoices",
      link: "invoices",
      icon: <RequestPageRounded />,
      role: ["Admin"], // Admin only
    },
    {
      text: "Reports",
      link: "reports",
      icon: <AssessmentRounded />,
      role: ["Admin"], // Admin only
    },
    {
      text: "SETTINGS",
      link: "",
      icon: null,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "Members",
      link: "members",
      icon: <GroupsRounded />,
      role: ["Admin"], // Admin only
    },
    {
      text: "Settings",
      link: "settings",
      icon: <SettingsRounded />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    //{
    //  text: "Changelog",
    //  link: "changelog",
    //  icon: <PublishedWithChangesIcon />,
    //  role: ["Admin", "Member"], // Visible to all roles
    //},
    {
      text: "Server",
      link: "server",
      icon: <TerminalRoundedIcon />,
      role: ["Admin"], // Admin only — logs + infrastructure health
    },
    {
      text: "Help",
      link: "help",
      icon: <HelpCenterRoundedIcon />,
      role: ["Admin", "Member"], // Visible to all roles
    },
  ];

  const { pathname } = useLocation();

  // Refresh the badges on navigation so leaving a screen whose data changed
  // (e.g. Expenses) updates the sidebar without a page reload.
  useEffect(() => {
    fetchDraftExpensesCount();
    fetchVideoCommentCount();
  }, [pathname, fetchDraftExpensesCount, fetchVideoCommentCount]);
  const [active, setActive] = useState("");

  // Fixed toggle function that handles both collapsing and closing
  const handleToggleSidebar = () => {
    // Prevent multiple clicks during animation
    if (isAnimating) return;

    // On mobile: simply close the sidebar (single state behavior)
    if (!isNonMobile) {
      setActiveMenu(false);
      return;
    }

    // On desktop: toggle between open, collapsed, and expanded states
    if (!activeMenu) {
      // If sidebar is closed, open it fully
      setActiveMenu(true);
      setContextIsCollapsed(false);
    } else if (!contextIsCollapsed) {
      // If sidebar is fully open, collapse it
      setContextIsCollapsed(true);
    } else if (contextIsCollapsed) {
      // If sidebar is collapsed, expand it (not close it)
      setContextIsCollapsed(false);
    }
  };

  // Handle navigation and close sidebar on mobile
  const handleNavigation = (link, params) => {
    if (!link) return; // Don't navigate for section headers

    if (params) navigate(`/${link}`, { state: params });
    else navigate(`/${link}`);

    setActive(link);

    // Close sidebar on mobile
    if (!isNonMobile) {
      setActiveMenu(false);
    }
  };

  // 2026-10-30 (Amine): a summary badge click filters the grid instead of just
  // opening the list. The badge sits inside a clickable MenuItem, so BOTH
  // stopPropagation and preventDefault are required or the parent wins.
  const handleSummaryBadgeNavigation = (event, summaryBadge) => {
    event.preventDefault();
    event.stopPropagation();

    navigate(
      buildSummaryFilterUrl(
        summaryBadge.path,
        summaryBadge.itemIds,
        summaryBadge.itemMonths,
      ),
    );

    // Close the sidebar on mobile, exactly like a normal menu navigation.
    if (!isNonMobile) {
      setActiveMenu(false);
    }
  };

  // Update active state based on the first segment of the url
  useEffect(() => {
    const pathParts = pathname.split("/");
    setActive(pathParts[1]);
  }, [pathname]);

  // Filter navItems based on user role and restrictedTo
  const filteredNavItems = navItems.filter((item) => {
    // If no role is specified or the user's role is included in the item's roles
    const roleMatch = !item.role || item.role.includes(user?.Role || "Member");

    // If restrictedTo is specified, check if user name matches
    const restrictedMatch =
      !item.restrictedTo || item.restrictedTo.includes(user?.Name);

    return roleMatch && restrictedMatch;
  });

  // Check if a section header has any visible children
  const sectionHasVisibleItems = (sectionText) => {
    const sectionIndex = filteredNavItems.findIndex(
      (item) => item.text === sectionText && !item.icon,
    );
    if (sectionIndex === -1) return false;

    // Look for the next section header or end of array
    let nextSectionIndex = filteredNavItems.findIndex(
      (item, index) => index > sectionIndex && !item.icon,
    );
    if (nextSectionIndex === -1) nextSectionIndex = filteredNavItems.length;

    // Check if there are any items between this section header and the next
    return nextSectionIndex > sectionIndex + 1;
  };

  return (
    <>
      <Box
        component="nav"
        sx={{
          width: activeMenu ? currentDrawerWidth : 0,
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 1200,
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.easeInOut,
              duration: ANIMATION_SPEED, // Animation for drawer width
            }),
        }}
      >
        {activeMenu && (
          <Drawer
            open={activeMenu}
            variant={!isNonMobile ? "temporary" : "permanent"}
            anchor="left"
            onClose={() => !isNonMobile && setActiveMenu(false)}
            PaperProps={{
              elevation: 10,
              sx: {
                width: currentDrawerWidth,
                border: "none",
                boxSizing: "border-box",
                transition: (theme) =>
                  theme.transitions.create("width", {
                    easing: theme.transitions.easing.easeInOut,
                    duration: ANIMATION_SPEED, // Animation for drawer width
                  }),
              },
            }}
            sx={{
              width: currentDrawerWidth,
              "& .MuiDrawer-paper": {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.background.default,
                width: currentDrawerWidth,
                // The paper matches the viewport; the nav list is the ONLY
                // scroll region (prevents an outer/paper-level scrollbar on
                // mobile, where viewport-height units overflow the visible area).
                overflow: "hidden",
              },
            }}
          >
            {/* Main container with flex layout */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
              }}
            >
              {/* Header section */}
              <Box
                p={isCollapsed ? 1 : 2}
                pb={isCollapsed ? 1 : 3}
                sx={{ flexShrink: 0 }}
              >
                <FlexBetween color={theme.palette.primary.main}>
                  {/* Logo with "CE Portal" text */}
                  {isCollapsed ? (
                    // Collapsed: Only logo, centered
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <img
                        src="/assets/ce-logo.svg"
                        alt="CE Portal Logo"
                        style={{
                          height: "32px",
                          width: "auto",
                          maxWidth: "150px",
                        }}
                      />
                    </Box>
                  ) : (
                    // Expanded: Logo with "CE Portal" text
                    <Box
                      sx={{
                        opacity: textVisible ? 1 : 0,
                        transition: `opacity ${ANIMATION_SPEED}ms ease-in-out`,
                        whiteSpace: "nowrap",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <img
                        src="/assets/ce-logo.svg"
                        alt="CE Portal Logo"
                        style={{
                          height: "32px",
                          width: "auto",
                          maxWidth: "150px",
                        }}
                      />

                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ color: theme.palette.primary.main }}
                      >
                        CE Portal
                      </Typography>
                    </Box>
                  )}
                </FlexBetween>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  px: isCollapsed ? 0 : 0,
                }}
              >
                {filteredNavItems.map((item, index) => {
                  // Skip section headers that don't have any visible items
                  if (!item.icon && !sectionHasVisibleItems(item.text)) {
                    return null;
                  }

                  // Section header
                  if (!item.icon) {
                    // If section header has a link, make it clickable
                    if (item.link) {
                      return (
                        <React.Fragment key={`section-${index}`}>
                          {isCollapsed &&
                            index > 0 &&
                            filteredNavItems[index - 1]?.icon && (
                              <Divider sx={{ my: 1, mx: 0.5 }} />
                            )}
                          <Box
                            onClick={() => handleNavigation(item.link)}
                            sx={{
                              mx: 2,
                              mt: 0.5,
                              mb: 0.125,
                              cursor: "pointer",
                              opacity: textVisible ? 1 : 0,
                              transition: `opacity ${ANIMATION_SPEED}ms ease-in-out`,
                              "&:hover": {
                                "& .MuiTypography-root": {
                                  color: theme.palette.primary.light,
                                },
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.primary.main,
                                fontWeight: "bold",
                                fontSize: 13,
                              }}
                            >
                              {item.text}
                            </Typography>
                          </Box>
                        </React.Fragment>
                      );
                    }
                    // Non-clickable section header
                    return (
                      <React.Fragment key={`section-${index}`}>
                        {isCollapsed &&
                          index > 0 &&
                          filteredNavItems[index - 1]?.icon && (
                            <Divider sx={{ my: 1, mx: 0.5 }} />
                          )}
                        <SectionHeader
                          text={item.text}
                          isCollapsed={isCollapsed}
                          textVisible={textVisible}
                        />
                      </React.Fragment>
                    );
                  }

                  // 2026-10-30 (Amine): the three summary badges open their own
                  // list pre-filtered to exactly the assignments they counted.
                  // Every other badge keeps its hover-only behaviour.
                  const summaryBadge = (() => {
                    if (item.text === "Interpretations") {
                      return {
                        path: "/interpretations",
                        itemIds: interpretationCompletion.itemIds,
                        itemMonths: interpretationCompletion.itemMonths,
                      };
                    }
                    if (item.text === "Translations") {
                      return {
                        path: "/translations",
                        itemIds: deadlineSummary.translations.itemIds,
                        itemMonths: deadlineSummary.translations.itemMonths,
                      };
                    }
                    if (item.text === "Other Services") {
                      return {
                        path: "/otherServices",
                        itemIds: deadlineSummary.other.itemIds,
                        itemMonths: deadlineSummary.other.itemMonths,
                      };
                    }
                    return null;
                  })();

                  // Menu item with icon
                  const menuItem = (
                    <MenuItem
                      key={item.text}
                      icon={item.icon}
                      text={item.text}
                      active={active}
                      link={item.link}
                      onClick={() => handleNavigation(item.link, item.params)}
                      isCollapsed={isCollapsed}
                      textVisible={textVisible}
                      badgeCount={
                        item.text === "Tasks"
                          ? taskCount
                          : item.text === "Invoices"
                            ? unbilledCount
                            : item.text === "Expenses"
                              ? draftExpensesCount
                              : item.text === "Server"
                                ? serverIssues.length
                                : item.text === "Video Library"
                                  ? videoCommentCount
                                  : item.text === "Interpretations"
                                    ? interpretationCompletion.active
                                      ? interpretationCompletion.count
                                      : 0
                                    : item.text === "Translations"
                                      ? deadlineSummary.translations.outstanding
                                      : item.text === "Other Services"
                                        ? deadlineSummary.other.outstanding
                                        : 0
                      }
                      badgeTooltip={
                        item.text === "Expenses"
                          ? draftExpensesTooltip
                          : item.text === "Server"
                            ? serverTooltip
                            : item.text === "Video Library"
                              ? videoCommentTooltip
                              : item.text === "Interpretations"
                                ? interpretationTooltip(interpretationCompletion)
                                : item.text === "Translations"
                                  ? deadlineTooltip(
                                      deadlineSummary.translations,
                                      "translations",
                                    )
                                  : item.text === "Other Services"
                                    ? deadlineTooltip(
                                        deadlineSummary.other,
                                        "other",
                                      )
                                    : ""
                      }
                      badgeInteractive={Boolean(summaryBadge)}
                      onBadgeClick={
                        summaryBadge
                          ? (event) =>
                              handleSummaryBadgeNavigation(event, summaryBadge)
                          : undefined
                      }
                      badgeSeverity={
                        item.text === "Server"
                          ? serverBadgeSeverity
                          : item.text === "Interpretations"
                            ? "warning"
                            : item.text === "Translations"
                              ? deadlineSeverity(deadlineSummary.translations)
                              : item.text === "Other Services"
                                ? deadlineSeverity(deadlineSummary.other)
                                : null
                      }
                    />
                  );

                  // Wrap with HtmlTooltip in collapsed mode
                  return isCollapsed ? (
                    <HtmlTooltip
                      key={`tooltip-${item.text}`}
                      title={item.text}
                      placement="right"
                    >
                      <Box>{menuItem}</Box>
                    </HtmlTooltip>
                  ) : (
                    menuItem
                  );
                })}
              </Box>

              {/* Bottom navigation - will stay at the bottom */}
              <Box sx={{ flexShrink: 0 }}>
                <Divider sx={{ my: 1 }} />

                {isCollapsed ? (
                  // Collapsed view: Open Sidebar button
                  <Box sx={{ mx: 0.5, my: 0.5 }}>
                    {/* Open Sidebar Button */}
                    <HtmlTooltip title="Open Sidebar" placement="right">
                      <IconButton
                        onClick={handleToggleSidebar}
                        disabled={isAnimating}
                        sx={{
                          width: "100%",
                          borderRadius: "12px",
                          p: 1.5,
                          justifyContent: "center",
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.buttonHover,
                          },
                        }}
                      >
                        <MenuRoundedIcon />
                      </IconButton>
                    </HtmlTooltip>
                  </Box>
                ) : (
                  // Expanded view: Close Sidebar button
                  <Box
                    sx={{
                      display: "flex",
                      mx: 2,
                      my: 0.5,
                    }}
                  >
                    {/* Close Sidebar Button */}
                    <Box
                      sx={{
                        width: "100%",
                      }}
                    >
                      <HtmlTooltip title="Close Sidebar" placement="right">
                        <IconButton
                          onClick={() =>
                            isNonMobile
                              ? handleToggleSidebar()
                              : setActiveMenu(false)
                          }
                          disabled={isAnimating}
                          sx={{
                            width: "100%",
                            borderRadius: "12px",
                            p: 1.5,
                            justifyContent: "center",
                            color: theme.palette.primary.main,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.primary.buttonHover,
                            },
                          }}
                        >
                          <MenuOpenRoundedIcon />
                        </IconButton>
                      </HtmlTooltip>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Drawer>
        )}
      </Box>
    </>
  );
};

export default Sidebar;
